
import { supabase } from "@/integrations/supabase/client";
import { saveAs } from "file-saver";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";

// Types for bookmark export
interface BookmarkNode {
  title: string;
  url?: string;
  children?: BookmarkNode[];
  dateAdded?: number;
  // The following is used for building the tree structure
  id?: string;
  parentId?: string | null;
  isFolder?: boolean;
}

/**
 * Fetches all collections and their content for the current user
 */
const fetchUserBookmarkData = async (userId: string, onProgress?: (stage: string, progress: number) => void) => {
  try {
    onProgress?.("Fetching collections...", 10);
    // Fetch all collections
    const { data: collections, error: collectionsError } = await supabase
      .from("collections")
      .select("*")
      .eq("user_id", userId)
      .order("position", { ascending: true });
    
    if (collectionsError) throw collectionsError;
    
    // Extract collection IDs for filtering content relationships
    const collectionIds = collections?.map(collection => collection.id) || [];
    console.log(`Found ${collections?.length} collections with IDs: ${collectionIds.join(', ')}`);
    
    onProgress?.("Fetching content items...", 30);
    // Fetch all content items (bookmarks)
    const { data: contentItems, error: contentError } = await supabase
      .from("content_items")
      .select("*")
      .eq("user_id", userId)
      .eq("archived", false) // Don't include archived items
      .order("created_at", { ascending: false });
    
    if (contentError) throw contentError;
    console.log(`Found ${contentItems?.length} content items`);
    
    onProgress?.("Fetching content-collection relationships...", 50);
    // Fetch content-collection relationships - IMPORTANT: filter by the user's collection IDs
    const { data: contentCollections, error: relationshipError } = await supabase
      .from("content_collections")
      .select("*")
      .in("collection_id", collectionIds);
    
    if (relationshipError) throw relationshipError;
    console.log(`Found ${contentCollections?.length} content-collection relationships`);
    
    onProgress?.("Processing data...", 70);
    return { collections, contentItems, contentCollections };
  } catch (error: any) {
    console.error("Error fetching bookmark data:", error);
    throw new Error(`Failed to fetch bookmark data: ${error.message || "Unknown error"}`);
  }
};

/**
 * Builds a bookmark tree structure from collections and content items
 */
const buildBookmarkTree = (
  collections: any[],
  contentItems: any[],
  contentCollections: any[]
): BookmarkNode => {
  console.log(`Building tree with ${collections?.length} collections, ${contentItems?.length} content items, and ${contentCollections?.length} relationships`);
  
  // Create a map of collection ID to collection for quick lookup
  const collectionMap = new Map();
  collections.forEach(collection => {
    collectionMap.set(collection.id, {
      ...collection,
      isFolder: true,
      children: [],
      dateAdded: new Date(collection.created_at || Date.now()).getTime() / 1000,
    });
  });
  
  // Create a mapping of content items to their collections
  const contentToCollectionMap = new Map();
  contentCollections.forEach(rel => {
    if (!contentToCollectionMap.has(rel.content_id)) {
      contentToCollectionMap.set(rel.content_id, []);
    }
    contentToCollectionMap.get(rel.content_id).push(rel.collection_id);
  });
  
  // Log the mappings for debugging
  console.log(`Content to collection mapping created with ${contentToCollectionMap.size} entries`);
  
  // Create root node
  const rootNode: BookmarkNode = {
    title: "Bookmarks",
    isFolder: true,
    children: []
  };
  
  // Add all collections as direct children of root
  collections.forEach(collection => {
    const node = {
      title: collection.name,
      isFolder: true,
      children: [],
      id: collection.id,
      dateAdded: new Date(collection.created_at || Date.now()).getTime() / 1000,
    };
    rootNode.children = rootNode.children || [];
    rootNode.children.push(node);
    // Also add to map for easy lookup
    collectionMap.set(collection.id, node);
  });
  
  // Add content items to their respective collections
  contentItems.forEach(item => {
    const bookmarkNode: BookmarkNode = {
      title: item.title || "Unnamed Bookmark",
      url: item.url,
      dateAdded: new Date(item.created_at || Date.now()).getTime() / 1000,
    };
    
    // Find which collections this item belongs to
    const collectionIds = contentToCollectionMap.get(item.id) || [];
    
    if (collectionIds.length > 0) {
      // Add to each collection this item belongs to
      collectionIds.forEach(collectionId => {
        if (collectionMap.has(collectionId)) {
          const collection = collectionMap.get(collectionId);
          collection.children = collection.children || [];
          collection.children.push(bookmarkNode);
          console.log(`Added "${bookmarkNode.title}" to collection "${collection.title}"`);
        }
      });
    } else {
      // Items not in any collection go directly under root
      rootNode.children = rootNode.children || [];
      rootNode.children.push(bookmarkNode);
      console.log(`Added "${bookmarkNode.title}" directly to root (no collection)`);
    }
  });
  
  // Log collection content summary
  rootNode.children?.forEach(collection => {
    if (collection.isFolder) {
      console.log(`Collection "${collection.title}" has ${collection.children?.length || 0} bookmarks`);
    }
  });
  
  return rootNode;
};

/**
 * Generates HTML markup for the bookmark tree
 */
const generateBookmarkHTML = (bookmarkTree: BookmarkNode): string => {
  // Create the HTML document header
  let html = `
<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file.
     It will be read and overwritten.
     DO NOT EDIT! -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>Bookmarks</TITLE>
<H1>Bookmarks</H1>
<DL><p>
`;

  // Recursive function to build HTML for each node
  const buildNodeHTML = (node: BookmarkNode, depth: number = 1): string => {
    const indent = '    '.repeat(depth);
    let nodeHtml = '';
    
    if (node.isFolder) {
      // It's a folder
      const dateAttr = node.dateAdded ? ` ADD_DATE="${Math.floor(node.dateAdded)}"` : '';
      nodeHtml += `${indent}<DT><H3${dateAttr}>${node.title}</H3>\n`;
      nodeHtml += `${indent}<DL><p>\n`;
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          nodeHtml += buildNodeHTML(child, depth + 1);
        });
      }
      
      nodeHtml += `${indent}</DL><p>\n`;
    } else if (node.url) {
      // It's a bookmark
      const dateAttr = node.dateAdded ? ` ADD_DATE="${Math.floor(node.dateAdded)}"` : '';
      nodeHtml += `${indent}<DT><A HREF="${node.url}"${dateAttr}>${node.title}</A>\n`;
    }
    
    return nodeHtml;
  };
  
  // Build HTML for each child of the root
  if (bookmarkTree.children && bookmarkTree.children.length > 0) {
    bookmarkTree.children.forEach(child => {
      html += buildNodeHTML(child);
    });
  }
  
  // Close the document
  html += '</DL><p>\n';
  
  return html;
};

/**
 * Main function to export bookmarks to HTML file
 */
export const exportBookmarksToHtml = async (
  userId: string,
  onProgress?: (stage: string, progress: number) => void
): Promise<boolean> => {
  try {
    // Fetch all necessary data
    const { collections, contentItems, contentCollections } = await fetchUserBookmarkData(userId, onProgress);
    
    console.log(`Building bookmark tree with ${collections?.length} collections and ${contentItems?.length} content items`);
    onProgress?.("Building bookmark tree...", 80);
    
    // Build the bookmark tree
    const bookmarkTree = buildBookmarkTree(collections, contentItems, contentCollections);
    
    // Log the built tree structure for debugging
    console.log("Bookmark tree built:", 
      JSON.stringify({
        rootTitle: bookmarkTree.title,
        childrenCount: bookmarkTree.children?.length || 0,
        firstLevelFolders: bookmarkTree.children?.map(c => ({
          title: c.title,
          isFolder: c.isFolder,
          childrenCount: c.children?.length || 0
        }))
      }, null, 2)
    );
    
    onProgress?.("Generating HTML...", 90);
    // Generate HTML
    const html = generateBookmarkHTML(bookmarkTree);
    
    onProgress?.("Creating download...", 95);
    // Create file for download
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const filename = `bookmarks_export_${new Date().toISOString().slice(0, 10)}.html`;
    
    // Trigger file download
    saveAs(blob, filename);
    
    onProgress?.("Export complete", 100);
    return true;
  } catch (error: any) {
    console.error("Error exporting bookmarks:", error);
    throw new Error(`Failed to export bookmarks: ${error.message || "Unknown error"}`);
  }
};
